import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Heart, ShieldCheck, Compass, Sparkles } from "lucide-react";

// Somveil three-bar mark — matches Home/Quiz/Results
function Mark({ color = "#4FBEBE", size = 22 }) {
  return (
    <svg viewBox="0 0 672 727" width={size} height={(size * 727) / 672} aria-hidden="true">
      <g fill={color}>
        <rect x="36" y="36" width="600" height="135" rx="29.7" />
        <rect x="126" y="296" width="420" height="135" rx="29.7" />
        <rect x="36" y="556" width="600" height="135" rx="29.7" />
      </g>
    </svg>
  );
}

const VALUES = [
  {
    icon: <Compass size={20} />,
    title: "Clarity first",
    desc: "Plain-language answers, no medical jargon. If a nine-year-old couldn't understand it, we rewrite it until they can.",
  },
  {
    icon: <ShieldCheck size={20} />,
    title: "Credentials, always",
    desc: "Every specialist is medical-council-verified before they appear on Somveil. We re-check credentials each year.",
  },
  {
    icon: <Heart size={20} />,
    title: "Never sell your data",
    desc: "Your quiz answers are yours. We don't sell, share, or advertise against your health information — ever.",
  },
  {
    icon: <Sparkles size={20} />,
    title: "Referral, not diagnosis",
    desc: "We don't pretend to be doctors. We're the bridge between suspicion and specialist — nothing more, nothing less.",
  },
];

const NUMBERS = [
  { n: "104M+", l: "Indians of working age likely to have OSA (AIIMS, 2023)" },
  { n: "9 in 10",   l: "Women with sleep apnea in India who go undiagnosed" },
  { n: "2 min",     l: "To take our validated risk quiz — free, private, no sign-up" },
];

export default function About() {
  return (
    <div className="ab-root" data-testid="about-page">
      <Nav theme="dark" />

      {/* HERO */}
      <section className="ab-hero">
        <div aria-hidden="true" className="ab-hero-orb ab-hero-orb-1" />
        <div aria-hidden="true" className="ab-hero-orb ab-hero-orb-2" />
        <div className="ab-inner">
          <div className="ab-hero-eyebrow">
            <Mark color="#4FBEBE" size={20} />
            <span>About Somveil</span>
          </div>
          <h1 className="ab-hero-h" data-testid="about-headline">
            We&apos;re here to help India<br />
            <em>see its sleep, clearly.</em>
          </h1>
          <p className="ab-hero-sub">
            Somveil is India&apos;s first dedicated sleep apnea discovery platform. We built it because tens of millions of Indians spend their lives exhausted, foggy, and unwell — and never learn why. That ends here.
          </p>
        </div>
      </section>

      {/* WHY WE EXIST */}
      <section className="ab-why">
        <div className="ab-inner ab-inner-narrow">
          <div className="hn-eyebrow-dark">Why we exist</div>
          <h2 className="ab-h2 dark">Sleep apnea is India&apos;s most under-diagnosed common condition.</h2>
          <div className="ab-lede-block">
            <p>
              Over 100 million Indians of working age are estimated to have obstructive sleep apnea. Most of them don&apos;t know it. They wake up unrefreshed, drift through their days, and are told it&apos;s stress, or age, or life. It&apos;s often none of those things.
            </p>
            <p>
              Untreated, sleep apnea triples the risk of stroke, doubles the risk of heart disease, worsens diabetes control, and quietly steals up to a third of a person&apos;s cognitive performance. And yet the diagnostic pathway in India is opaque — patients don&apos;t know it exists, don&apos;t know who to see, and don&apos;t know what to expect.
            </p>
            <p>
              Somveil closes that gap. A calm, credible, two-minute risk quiz — followed by a warm hand-off to verified specialists in your city. Nothing sold, nothing pushed. Just the next honest step, whenever you&apos;re ready to take it.
            </p>
          </div>
        </div>
      </section>

      {/* NUMBERS */}
      <section className="ab-numbers">
        <div className="ab-inner">
          <div className="ab-num-grid">
            {NUMBERS.map((n, i) => (
              <div key={n.n} className="ab-num-card" data-testid={`about-num-${i}`}>
                <div className="ab-num-n">{n.n}</div>
                <div className="ab-num-l">{n.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE STAND FOR */}
      <section className="ab-values">
        <div className="ab-inner">
          <div className="hn-eyebrow-teal">What we stand for</div>
          <h2 className="ab-h2">Four commitments. Non-negotiable.</h2>
          <div className="ab-values-grid">
            {VALUES.map((v, i) => (
              <div key={v.title} className="ab-value-card" data-testid={`about-value-${i}`}>
                <div className="ab-value-icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE HELP */}
      <section className="ab-how">
        <div className="ab-inner ab-inner-narrow">
          <div className="hn-eyebrow-dark">What Somveil actually does</div>
          <h2 className="ab-h2 dark">Three things. Well.</h2>
          <div className="ab-how-list">
            <div className="ab-how-row" data-testid="about-how-1">
              <span className="ab-how-idx">01</span>
              <div>
                <h4>A validated 2-minute risk quiz</h4>
                <p>Built on STOP-BANG and Epworth — the two most-used screening tools in sleep medicine — plus custom rules refined with Indian sleep specialists. It gives you a clear, plain-language read on your risk.</p>
              </div>
            </div>
            <div className="ab-how-row" data-testid="about-how-2">
              <span className="ab-how-idx">02</span>
              <div>
                <h4>A directory of verified sleep specialists</h4>
                <p>Every doctor on Somveil is credential-checked and reviewed. We show you who they are, where they practise, and what to expect — so choosing feels informed, not intimidating.</p>
              </div>
            </div>
            <div className="ab-how-row" data-testid="about-how-3">
              <span className="ab-how-idx">03</span>
              <div>
                <h4>A home sleep study, booked in minutes</h4>
                <p>When you&apos;re ready to get tested, we handle the logistics. A certified technician arrives at your home, sets up a small monitor, and your chosen specialist reviews the results.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARENT COMPANY */}
      <section className="ab-parent">
        <div className="ab-inner">
          <div className="ab-parent-card">
            <div aria-hidden="true" className="ab-parent-orb" />
            <div className="ab-parent-inner">
              <div className="hn-eyebrow-teal">Behind Somveil</div>
              <h2 className="ab-parent-h">A HMH Labz LLP product.</h2>
              <p>
                Somveil is a brand and platform owned and operated by <a href="https://www.hmhlabz.com" target="_blank" rel="noopener noreferrer" className="ab-parent-link">HMH Labz LLP</a> — a health-technology venture registered in Chennai, India under the Limited Liability Partnership Act, 2008. HMH Labz builds category-defining, evidence-first health experiences for India and the wider Asia region.
              </p>
              <p>
                We are not a hospital, not a lab, and not a telemedicine service. We are the layer between a suspicion something is wrong and the qualified specialist who can confirm it — and we are proud to be exactly that.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="ab-final">
        <div aria-hidden="true" className="ab-final-orb" />
        <div className="ab-final-inner">
          <div className="ab-final-mark"><Mark color="#4FBEBE" size={40} /></div>
          <h2 className="ab-final-h">The next step is quiet, private, and two minutes long.</h2>
          <p>Take our free risk quiz and finally see your sleep, clearly.</p>
          <Link to="/quiz" className="hn-btn-teal large" data-testid="about-final-cta">
            Take the free 2-minute quiz <span className="arr">&rarr;</span>
          </Link>
          <div className="ab-final-meta">Free &nbsp;·&nbsp; Private &nbsp;·&nbsp; No sign-up</div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
